import React, { useState } from 'react';
import { FiX, FiCalendar } from 'react-icons/fi';

import { useRejectionCategories } from '@/features/onboarding/hooks';
import { TypeAnalysis } from '@/features/onboarding/types';

export interface RejectedFilterData {
  startDate: string;
  endDate: string;
  typeDisaproved: number | null;
  categoryRejectedName: string;
}

interface RejectedFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: RejectedFilterData) => void;
}

export const RejectedFilterModal: React.FC<RejectedFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
}) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [typeDisaproved, setTypeDisaproved] = useState<number | null>(null);
  const [categoryRejectedName, setCategoryRejectedName] = useState('');

  // Fetch rejection categories from API
  const { data: categories, isLoading: categoriesLoading } = useRejectionCategories();

  if (!isOpen) return null;

  const handleApply = () => {
    onApply({
      startDate,
      endDate,
      typeDisaproved,
      categoryRejectedName,
    });
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setTypeDisaproved(null);
    setCategoryRejectedName('');
  };

  // Rejection type options with numeric values matching API
  const rejectionTypeOptions = [
    { value: TypeAnalysis.MANUAL, label: 'Manual' },
    { value: TypeAnalysis.AUTOMATIC, label: 'Automática' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-800">Filtros</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Data inicial e Data final - Grid 2 colunas */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">
                Data inicial
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                  }}
                  placeholder="Inserir"
                  className="w-full px-3 py-2 pr-9 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
                <FiCalendar className="absolute right-2.5 top-1/2 -translate-y-1/2 text-purple-600 w-4 h-4 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">Data final</label>
              <div className="relative">
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                  }}
                  placeholder="Inserir"
                  className="w-full px-3 py-2 pr-9 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
                <FiCalendar className="absolute right-2.5 top-1/2 -translate-y-1/2 text-purple-600 w-4 h-4 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Reprovação - Linha inteira */}
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Tipo de reprovação
            </label>
            <div className="relative">
              <select
                value={typeDisaproved ?? ''}
                onChange={(e) => {
                  setTypeDisaproved(e.target.value ? Number(e.target.value) : null);
                }}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none text-sm text-neutral-900"
              >
                <option value="">Selecione</option>
                {rejectionTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-neutral-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Categoria do motivo - Linha inteira */}
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">
              Categoria do motivo
            </label>
            <div className="relative">
              <select
                value={categoryRejectedName}
                onChange={(e) => {
                  setCategoryRejectedName(e.target.value);
                }}
                disabled={categoriesLoading}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none text-sm text-neutral-900 disabled:bg-neutral-100"
              >
                <option value="">{categoriesLoading ? 'Carregando...' : 'Selecione'}</option>
                {categories?.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-neutral-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-4 border-t border-neutral-200">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm text-neutral-600 hover:text-neutral-800 transition-colors"
          >
            Limpar filtros
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
          >
            Aplicar filtros
          </button>
        </div>
      </div>
    </div>
  );
};
